/**
 * CollaborativeIDE.jsx
 * 
 * Real-time collaborative code editor powered by Monaco Editor + Socket.io.
 * 
 * Features:
 * - Multi-user real-time coding with conflict-free sync
 * - Syntax highlighting for JS, Python, Java, C++, SQL
 * - Language switching with code persistence
 * - File tabs support
 * - Read-only mode for recruiter playback
 * - Code execution via Judge0 (Run button)
 * - stdin input modal
 * - Output console with error display
 * - Cursor sync & typing indicators
 * - Auto-save & session recovery
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { API_BASE } from '../../utils/config';
import { trackEvent } from '../../utils/analytics';

// ====== LANGUAGE CONFIG ======
const LANGUAGES = [
  { key: 'javascript', label: 'JavaScript', monacoId: 'javascript', icon: '🟨' },
  { key: 'python', label: 'Python', monacoId: 'python', icon: '🐍' },
  { key: 'java', label: 'Java', monacoId: 'java', icon: '☕' },
  { key: 'cpp', label: 'C++', monacoId: 'cpp', icon: '⚡' },
  { key: 'sql', label: 'SQL', monacoId: 'sql', icon: '🗃️' },
];

// ====== STYLES ======
const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#1e1e1e',
    border: '1px solid var(--border-color, #e2e8f0)',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: '#252526',
    borderBottom: '1px solid #3c3c3c',
    flexWrap: 'wrap',
  },
  languageTabs: {
    display: 'flex',
    gap: '2px',
    flex: 1,
  },
  languageTab: {
    padding: '5px 12px',
    borderRadius: '6px 6px 0 0',
    border: 'none',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  activeTab: {
    background: '#1e1e1e',
    color: '#e2e8f0',
  },
  inactiveTab: {
    background: 'transparent',
    color: '#808080',
  },
  actionButton: {
    padding: '5px 14px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontFamily: 'Inter, Arial, sans-serif',
    transition: 'all 0.15s ease',
  },
  runButton: {
    background: '#22c55e',
    color: '#fff',
  },
  runButtonDisabled: {
    background: '#374151',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  stdinButton: {
    background: '#3b82f6',
    color: '#fff',
  },
  closeButton: {
    background: 'transparent',
    color: '#808080',
    border: '1px solid #555',
  },
  editorWrapper: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  outputPanel: {
    borderTop: '1px solid #3c3c3c',
    background: '#1a1a2e',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  outputHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 12px',
    background: '#16213e',
    borderBottom: '1px solid #2a2a4a',
  },
  outputTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  outputContent: {
    padding: '8px 12px',
    fontSize: '12px',
    fontFamily: 'Consolas, "Courier New", monospace',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  presenceBar: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  typingIndicator: {
    fontSize: '11px',
    color: '#6366f1',
    fontStyle: 'italic',
    animation: 'ide-pulse 1.5s ease-in-out infinite',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 12px',
    background: '#007acc',
    fontSize: '11px',
    color: '#fff',
    fontWeight: '500',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
  },
  modalContent: {
    background: '#252526',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '480px',
    width: '90%',
    border: '1px solid #3c3c3c',
  },
  textarea: {
    width: '100%',
    minHeight: '80px',
    background: '#1e1e1e',
    color: '#e2e8f0',
    border: '1px solid #3c3c3c',
    borderRadius: '6px',
    padding: '8px',
    fontSize: '13px',
    fontFamily: 'Consolas, monospace',
    resize: 'vertical',
    marginTop: '8px',
    boxSizing: 'border-box',
  },
};

const AVATAR_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
const getAvatarColor = (name) => {
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/**
 * @param {Object} props
 * @param {Object} props.socket - Active Socket.io instance
 * @param {string} props.roomId - Interview room ID
 * @param {string} props.userName - Current user's display name
 * @param {string} props.role - 'candidate' | 'interviewer' | 'recruiter'
 * @param {boolean} [props.readOnly=false] - Read-only mode
 * @param {function} [props.onClose] - Callback when IDE is closed
 * @param {function} [props.onKeystroke] - Callback for keystroke recording (Phase 5)
 */
const CollaborativeIDE = ({
  socket,
  roomId,
  userName,
  role,
  readOnly = false,
  onClose,
  onKeystroke,
}) => {
  const [activeLanguage, setActiveLanguage] = useState('javascript');
  const [code, setCode] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [showStdinModal, setShowStdinModal] = useState(false);
  const [stdin, setStdin] = useState('');
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [collaborators, setCollaborators] = useState(new Map());
  const [showOutput, setShowOutput] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  const editorRef = useRef(null);
  const isRemoteUpdateRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const lastProcessedCodeRef = useRef('');
  const socketEmitThrottleRef = useRef(null); // Throttle socket broadcasts
  const pendingCodeRef = useRef(null);         // Always holds the LATEST code to broadcast
  const iAmTypingRef = useRef(false);         // Track if we've already notified others we are typing

  // ====== SOCKET EVENT HANDLERS ======
  useEffect(() => {
    if (!socket || !roomId) return;

    socket.emit('ide:join', { roomId, userName, role });

    const handleStateRestore = (data) => {
      console.log(`💻 IDE state restored: ${data.language}`);
      setActiveLanguage(data.language);

      const newCode = (data.code || '').replace(/\r\n/g, '\n');
      isRemoteUpdateRef.current = true;
      lastProcessedCodeRef.current = newCode;
      setCode(newCode);
      if (window.ideUpdateTimeout) clearTimeout(window.ideUpdateTimeout);
      window.ideUpdateTimeout = setTimeout(() => { isRemoteUpdateRef.current = false; }, 500);
    };

    const handleCodeChange = (data) => {
      if (data.senderId === socket.id) return;
      
      const normalizedIncoming = (data.content || '').replace(/\r\n/g, '\n');
      if (normalizedIncoming === lastProcessedCodeRef.current) return; // Prevent infinite loop echo
      
      isRemoteUpdateRef.current = true;
      lastProcessedCodeRef.current = normalizedIncoming;
      setCode(normalizedIncoming);
      
      if (window.ideUpdateTimeout) clearTimeout(window.ideUpdateTimeout);
      window.ideUpdateTimeout = setTimeout(() => { isRemoteUpdateRef.current = false; }, 500);
    };

    const handleLanguageChanged = (data) => {
      setActiveLanguage(data.language);
      
      const newCode = (data.code || '').replace(/\r\n/g, '\n');
      isRemoteUpdateRef.current = true;
      lastProcessedCodeRef.current = newCode;
      setCode(newCode);
      
      if (window.ideUpdateTimeout) clearTimeout(window.ideUpdateTimeout);
      window.ideUpdateTimeout = setTimeout(() => { isRemoteUpdateRef.current = false; }, 500);
    };

    const handleTyping = (data) => {
      setTypingUsers((prev) => {
        const next = new Map(prev);
        if (data.isTyping) {
          next.set(data.socketId, data.userName);
        } else {
          next.delete(data.socketId);
        }
        return next;
      });
    };

    const handleUserJoined = (data) => {
      setCollaborators((prev) => {
        const next = new Map(prev);
        next.set(data.socketId, { userName: data.userName, role: data.role });
        return next;
      });
    };

    const handleUserLeft = (data) => {
      setCollaborators((prev) => {
        const next = new Map(prev);
        next.delete(data.socketId);
        return next;
      });
      setTypingUsers((prev) => {
        const next = new Map(prev);
        next.delete(data.socketId);
        return next;
      });
    };

    const handleExecutionResult = (data) => {
      setOutput(data);
      setShowOutput(true);
      setIsRunning(false);
    };

    socket.on('ide:state-restore', handleStateRestore);
    socket.on('ide:code-change', handleCodeChange);
    socket.on('ide:language-changed', handleLanguageChanged);
    socket.on('ide:typing', handleTyping);
    socket.on('ide:user-joined', handleUserJoined);
    socket.on('ide:user-left', handleUserLeft);
    socket.on('ide:execution-result', handleExecutionResult);

    return () => {
      socket.off('ide:state-restore', handleStateRestore);
      socket.off('ide:code-change', handleCodeChange);
      socket.off('ide:language-changed', handleLanguageChanged);
      socket.off('ide:typing', handleTyping);
      socket.off('ide:user-joined', handleUserJoined);
      socket.off('ide:user-left', handleUserLeft);
      socket.off('ide:execution-result', handleExecutionResult);

      // Clean up timers on unmount
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (socketEmitThrottleRef.current) clearTimeout(socketEmitThrottleRef.current);
      if (window.ideUpdateTimeout) clearTimeout(window.ideUpdateTimeout);
    };
  }, [socket, roomId, userName, role]);

  // ====== CODE CHANGE HANDLER ======
  const handleEditorChange = useCallback(
    (value) => {
      const normalizedVal = (value || '').replace(/\r\n/g, '\n');
      
      if (isRemoteUpdateRef.current) return;
      if (readOnly) return;
      if (normalizedVal === lastProcessedCodeRef.current) return;

      lastProcessedCodeRef.current = normalizedVal;

      // CRITICAL FIX: DO NOT call setCode(value) here!
      // Allowing Monaco to hold its own internal state prevents cursor jumping.

      if (socket && roomId) {
        // === THROTTLED SOCKET EMIT (max 1 per 300ms) ===
        // Store the latest value so the throttle always sends up-to-date code
        pendingCodeRef.current = normalizedVal;

        if (!socketEmitThrottleRef.current) {
          // Schedule a flush in 300ms
          socketEmitThrottleRef.current = setTimeout(() => {
            const latest = pendingCodeRef.current;
            socketEmitThrottleRef.current = null;
            pendingCodeRef.current = null;

            if (latest !== null) {
              socket.emit('ide:code-change', {
                roomId,
                language: activeLanguage,
                content: latest,
              });
            }
          }, 300);
        }

        // === EFFICIENT TYPING INDICATOR ===
        // Only emit true once when starting, and false once when stopping.
        // This cuts out thousands of redundant websocket packets and React re-renders!
        if (!iAmTypingRef.current) {
          iAmTypingRef.current = true;
          socket.emit('ide:typing', { roomId, isTyping: true });
        }

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          iAmTypingRef.current = false;
          socket.emit('ide:typing', { roomId, isTyping: false });
        }, 2000);
      }

      // Keystroke recording callback
      if (onKeystroke) {
        onKeystroke({
          type: 'edit',
          language: activeLanguage,
          timestamp: Date.now(),
          contentLength: (value || '').length,
        });
      }
    },
    [socket, roomId, activeLanguage, readOnly, onKeystroke]
  );

  // ====== LANGUAGE SWITCH ======
  const handleLanguageSwitch = (lang) => {
    if (lang === activeLanguage) return;
    trackEvent('ide_language_changed', {
      from_language: activeLanguage,
      to_language: lang,
      room_id: roomId
    });
    if (socket && roomId) {
      socket.emit('ide:language-change', { roomId, language: lang });
    }
  };

  // ====== CODE EXECUTION ======
  const handleRun = async (customStdin) => {
    if (isRunning || readOnly) return;

    setIsRunning(true);
    setShowOutput(true);
    setOutput({
      status: 'running',
      stdout: '',
      stderr: '',
      statusDescription: 'Running...',
    });

    try {
      const currentCode = editorRef.current ? editorRef.current.getValue() : code;
      
      const response = await fetch(`${API_BASE}/api/code-runner/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: activeLanguage,
          sourceCode: currentCode,
          stdin: customStdin || stdin,
          roomId,
          executedBy: userName,
        }),
      });

      const data = await response.json();

      if (data.success && data.result) {
        setOutput(data.result);

        // Broadcast result to other participants
        if (socket && roomId) {
          socket.emit('ide:execution-result', {
            roomId,
            result: data.result,
          });
        }

        trackEvent('ide_run_code', {
          programming_language: activeLanguage,
          compile_status: data.result.status || 'success',
          executed_by: role,
          room_id: roomId
        });

        // Keystroke recording
        if (onKeystroke) {
          onKeystroke({
            type: 'run',
            language: activeLanguage,
            timestamp: Date.now(),
            status: data.result.status,
          });
        }
      } else {
        setOutput({
          status: 'error',
          stdout: '',
          stderr: data.error || 'Execution failed',
          statusDescription: 'Error',
        });
        trackEvent('ide_run_code', {
          programming_language: activeLanguage,
          compile_status: 'failed',
          executed_by: role,
          room_id: roomId
        });
      }
    } catch (error) {
      setOutput({
        status: 'error',
        stdout: '',
        stderr: `Network error: ${error.message}`,
        statusDescription: 'Error',
      });
      trackEvent('ide_run_code', {
        programming_language: activeLanguage,
        compile_status: 'error',
        executed_by: role,
        room_id: roomId
      });
    } finally {
      setIsRunning(false);
    }
  };

  // ====== EDITOR MOUNT ======
  const handleEditorMount = (editor) => {
    editorRef.current = editor;
  };

  // ====== RENDER ======
  const currentLang = LANGUAGES.find((l) => l.key === activeLanguage) || LANGUAGES[0];
  const typingList = Array.from(typingUsers.values());

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes ide-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        {/* Language tabs */}
        <div style={styles.languageTabs}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.key}
              onClick={() => handleLanguageSwitch(lang.key)}
              style={{
                ...styles.languageTab,
                ...(activeLanguage === lang.key ? styles.activeTab : styles.inactiveTab),
              }}
              title={lang.label}
            >
              <span>{lang.icon}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>

        {/* Presence */}
        <div style={styles.presenceBar}>
          {collaborators.size > 0 && (
            <span style={{ fontSize: '11px', color: '#808080' }}>
              {collaborators.size} online
            </span>
          )}
          {typingList.length > 0 && (
            <span style={styles.typingIndicator}>
              {typingList.join(', ')} typing...
            </span>
          )}
        </div>

        {/* Action buttons */}
        {!readOnly && (
          <>
            <button
              style={{
                ...styles.actionButton,
                ...styles.stdinButton,
              }}
              onClick={() => setShowStdinModal(true)}
              title="Provide Standard Input"
            >
              📥 stdin
            </button>
            <button
              style={{
                ...styles.actionButton,
                ...(isRunning ? styles.runButtonDisabled : styles.runButton),
              }}
              onClick={() => handleRun()}
              disabled={isRunning}
              title="Run Code"
            >
              {isRunning ? '⏳ Running...' : '▶ Run'}
            </button>
          </>
        )}

        {onClose && (
          <button
            style={{ ...styles.actionButton, ...styles.closeButton }}
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>

      {/* Monaco Editor */}
      <div style={styles.editorWrapper}>
        <Editor
          height="100%"
          language={currentLang.monacoId}
          value={code}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          theme="vs-dark"
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            folding: true,
            lineDecorationsWidth: 10,
            renderLineHighlight: 'all',
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
            smoothScrolling: true,
            padding: { top: 8 },
          }}
        />
      </div>

      {/* Output Panel */}
      {showOutput && output && (
        <div style={styles.outputPanel}>
          <div style={styles.outputHeader}>
            <span style={styles.outputTitle}>
              Output — {output.statusDescription || output.status}
              {output.executionTimeMs != null && ` (${output.executionTimeMs}ms)`}
              {output.memoryUsedKb != null && ` • ${(output.memoryUsedKb / 1024).toFixed(1)}MB`}
            </span>
            <button
              onClick={() => setShowOutput(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#808080',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ✕
            </button>
          </div>
          <div style={styles.outputContent}>
            {output.compileOutput && (
              <div style={{ color: '#ef4444', marginBottom: '8px' }}>
                <strong>Compilation Error:</strong>{'\n'}{output.compileOutput}
              </div>
            )}
            {output.stderr && (
              <div style={{ color: '#f59e0b', marginBottom: '8px' }}>
                <strong>Stderr:</strong>{'\n'}{output.stderr}
              </div>
            )}
            {output.stdout ? (
              <div style={{ color: '#22c55e' }}>{output.stdout}</div>
            ) : (
              !output.stderr && !output.compileOutput && output.status !== 'running' && (
                <div style={{ color: '#808080', fontStyle: 'italic' }}>No output</div>
              )
            )}
            {output.status === 'running' && (
              <div style={{ color: '#6366f1' }}>⏳ Executing code...</div>
            )}
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div style={styles.statusBar}>
        <span>{currentLang.icon} {currentLang.label}</span>
        <span>
          {readOnly ? '🔒 Read-only' : '✏️ Editing'}
          {saveStatus && ` • ${saveStatus}`}
        </span>
      </div>

      {/* stdin Modal */}
      {showStdinModal && (
        <div style={styles.modal} onClick={() => setShowStdinModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 8px', fontSize: '16px' }}>
              📥 Standard Input (stdin)
            </h3>
            <p style={{ color: '#808080', fontSize: '12px', margin: '0 0 12px' }}>
              Provide input that your program will read from stdin.
            </p>
            <textarea
              style={styles.textarea}
              value={stdin}
              onChange={(e) => setStdin(e.target.value)}
              placeholder="Enter input here..."
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
              <button
                style={{ ...styles.actionButton, ...styles.closeButton }}
                onClick={() => setShowStdinModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.actionButton, ...styles.runButton }}
                onClick={() => {
                  setShowStdinModal(false);
                  handleRun(stdin);
                }}
              >
                ▶ Run with Input
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborativeIDE;
